<template>
  <div class="ep-page max-w-6xl">

    <!-- ── Hero ───────────────────────────────────────────── -->
    <div class="rounded-2xl overflow-hidden relative"
         style="background: linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(124,58,237,0.14) 100%); border: 1px solid rgba(129,140,248,0.28)">
      <div class="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
           style="background: radial-gradient(circle, rgba(129,140,248,0.16) 0%, transparent 70%)"></div>

      <div class="relative px-5 sm:px-8 py-7">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-4">
            <div class="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                 style="background: rgba(99,102,241,0.25); border: 1px solid rgba(129,140,248,0.4)">
              <BookOpen class="w-7 h-7 text-indigo-200" />
            </div>
            <div>
              <h1 class="text-xl sm:text-2xl font-bold text-white">User Manual</h1>
              <p class="text-sm text-indigo-200/70 mt-0.5">Your complete guide to ExoTrack</p>
            </div>
          </div>
          <button @click="$router.back()" class="ep-btn ep-btn-secondary ep-btn-sm shrink-0">
            <ArrowLeft class="w-4 h-4" />
            <span class="hidden sm:inline">Back</span>
          </button>
        </div>

        <!-- Search -->
        <div class="mt-6 relative">
          <Search class="absolute left-4 top-1/2 -translate-y-1/2 w-[1.125rem] h-[1.125rem] text-slate-500 pointer-events-none" />
          <input
            v-model="searchQuery"
            @input="handleSearch"
            type="text"
            placeholder="Search for help articles, FAQs, or topics…"
            class="ep-input !pl-11 !pr-11 !py-3.5 !rounded-xl"
            style="background: rgba(6,10,24,0.55)"
          />
          <button v-if="searchQuery" @click="clearSearch"
                  class="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors">
            <X class="w-[1.125rem] h-[1.125rem]" />
          </button>

          <!-- Search Results Dropdown -->
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95">
            <div v-if="searchQuery && searchResults"
                 class="absolute z-20 w-full mt-2 rounded-2xl max-h-96 overflow-y-auto shadow-2xl scrollbar-thin"
                 style="background: rgba(11,16,31,0.98); border: 1px solid rgba(255,255,255,0.09)">
              <!-- Articles -->
              <div v-if="searchResults.articles && searchResults.articles.length > 0"
                   class="p-4" style="border-bottom: 1px solid rgba(255,255,255,0.06)">
                <h3 class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2.5">Articles</h3>
                <div class="space-y-1">
                  <button v-for="article in searchResults.articles" :key="article.id"
                          @click="openArticle(article.categoryId, article.id)"
                          class="w-full text-left p-3 rounded-xl hover:bg-indigo-500/10 transition-colors group">
                    <div class="flex items-start gap-3">
                      <FileText class="w-[1.125rem] h-[1.125rem] text-indigo-400 mt-0.5 shrink-0" />
                      <div class="min-w-0">
                        <p class="text-sm font-medium text-slate-200 group-hover:text-indigo-300 transition-colors">
                          {{ article.title }}
                        </p>
                        <p class="text-xs text-slate-500 mt-0.5">{{ article.category }}</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <!-- FAQs -->
              <div v-if="searchResults.faqs && searchResults.faqs.length > 0" class="p-4">
                <h3 class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2.5">FAQs</h3>
                <div class="space-y-1">
                  <button v-for="faq in searchResults.faqs" :key="faq.id"
                          @click="openFAQ(faq.id)"
                          class="w-full text-left p-3 rounded-xl hover:bg-indigo-500/10 transition-colors group">
                    <div class="flex items-start gap-3">
                      <HelpCircle class="w-[1.125rem] h-[1.125rem] text-emerald-400 mt-0.5 shrink-0" />
                      <p class="text-sm font-medium text-slate-200 group-hover:text-indigo-300 transition-colors">
                        {{ faq.question }}
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              <!-- No results -->
              <div v-if="(!searchResults.articles || searchResults.articles.length === 0) &&
                         (!searchResults.faqs || searchResults.faqs.length === 0)"
                   class="ep-empty">
                <AlertCircle class="w-10 h-10" />
                <p class="text-sm text-slate-400">No results found for "{{ searchQuery }}"</p>
                <p class="text-xs text-slate-600 mt-1">Try different keywords or browse categories below</p>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- ── Loading ────────────────────────────────────────── -->
    <div v-if="loading" class="py-20 text-center">
      <div class="ep-spinner mb-4"></div>
      <p class="text-sm text-slate-500">Loading user manual…</p>
    </div>

    <!-- ── Content ────────────────────────────────────────── -->
    <template v-else>
      <!-- Quick Tips -->
      <div v-if="quickTips && quickTips.length > 0"
           class="rounded-2xl p-5 sm:p-6"
           style="background: rgba(251,191,36,0.05); border: 1px solid rgba(251,191,36,0.2)">
        <div class="flex items-center gap-2.5 mb-4">
          <Lightbulb class="w-5 h-5 text-amber-400" />
          <h2 class="text-base font-semibold text-slate-100">Quick Tips</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div v-for="tip in quickTips" :key="tip.id"
               class="ep-card ep-card-hover p-4">
            <div class="flex items-start gap-3">
              <div class="ep-stat-icon ep-stat-amber !w-9 !h-9 !rounded-lg shrink-0">
                <component :is="getIconComponent(tip.icon)" class="w-[1.125rem] h-[1.125rem]" />
              </div>
              <div class="min-w-0">
                <h3 class="text-sm font-semibold text-slate-200 mb-1">{{ tip.title }}</h3>
                <p class="text-xs text-slate-500 leading-relaxed">{{ tip.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Categories -->
      <div>
        <h2 class="text-lg font-bold text-slate-100 mb-4">Browse by Category</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button v-for="category in categories" :key="category.id"
                  @click="selectCategory(category)"
                  class="ep-card ep-card-hover p-5 text-left group">
            <div class="flex items-start gap-4">
              <div class="ep-stat-icon ep-stat-indigo transition-transform duration-200 group-hover:scale-105">
                <component :is="getIconComponent(category.icon)" class="w-5 h-5" />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="text-base font-semibold text-slate-200 group-hover:text-indigo-300 transition-colors">
                  {{ category.title }}
                </h3>
                <p class="text-xs text-slate-500 mt-1 leading-relaxed">{{ category.description }}</p>
                <div class="flex items-center mt-2.5 text-indigo-400 text-xs font-medium">
                  <span>{{ category.articles.length }} articles</span>
                  <ChevronRight class="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- FAQs -->
      <div class="ep-card p-5 sm:p-6">
        <div class="flex items-center gap-2.5 mb-5">
          <HelpCircle class="w-5 h-5 text-emerald-400" />
          <h2 class="text-lg font-bold text-slate-100">Frequently Asked Questions</h2>
        </div>

        <!-- Category filter -->
        <div class="flex flex-wrap gap-2 mb-5">
          <button v-for="cat in faqCategories" :key="cat.value"
                  @click="selectedFAQCategory = cat.value"
                  :class="['px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-150',
                           selectedFAQCategory === cat.value
                             ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40'
                             : 'text-slate-500 hover:text-slate-300 border border-white/10 hover:border-white/20']">
            {{ cat.label }}
          </button>
        </div>

        <!-- FAQ list -->
        <div class="space-y-2.5">
          <div v-for="faq in filteredFAQs" :key="faq.id" :id="`faq-${faq.id}`"
               class="rounded-xl overflow-hidden"
               style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
            <button @click="toggleFAQ(faq.id)"
                    class="w-full flex items-center justify-between gap-4 p-4 text-left transition-colors hover:bg-white/[0.03]">
              <span class="text-sm font-medium text-slate-200">{{ faq.question }}</span>
              <ChevronDown :class="['w-[1.125rem] h-[1.125rem] text-slate-500 shrink-0 transition-transform duration-200',
                                    expandedFAQs.includes(faq.id) ? 'rotate-180' : '']" />
            </button>
            <transition
              enter-active-class="transition duration-200 ease-out"
              enter-from-class="transform opacity-0 -translate-y-2"
              enter-to-class="transform opacity-100 translate-y-0"
              leave-active-class="transition duration-150 ease-in"
              leave-from-class="transform opacity-100 translate-y-0"
              leave-to-class="transform opacity-0 -translate-y-2">
              <div v-if="expandedFAQs.includes(faq.id)" class="px-4 pb-4">
                <p class="text-sm text-slate-400 leading-relaxed">{{ faq.answer }}</p>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Troubleshooting -->
      <div class="ep-card p-5 sm:p-6">
        <div class="flex items-center gap-2.5 mb-5">
          <AlertCircle class="w-5 h-5 text-red-400" />
          <h2 class="text-lg font-bold text-slate-100">Troubleshooting</h2>
        </div>

        <div class="space-y-3">
          <div v-for="guide in troubleshooting" :key="guide.id"
               class="rounded-xl p-4"
               style="background: rgba(248,113,113,0.05); border: 1px solid rgba(248,113,113,0.18)">
            <h3 class="text-sm font-semibold text-slate-200 mb-2 flex items-start">
              <AlertTriangle class="w-[1.125rem] h-[1.125rem] text-red-400 mr-2.5 shrink-0 mt-0.5" />
              {{ guide.problem }}
            </h3>
            <p class="ml-7 text-sm text-slate-400 whitespace-pre-line leading-relaxed">{{ guide.solution }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- ── Category Modal ─────────────────────────────────── -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform opacity-0"
      enter-to-class="transform opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform opacity-100"
      leave-to-class="transform opacity-0">
      <div v-if="selectedCategory" class="ep-modal-backdrop" @click.self="closeModal">
        <div class="ep-modal max-w-4xl">
          <!-- Modal header -->
          <div class="px-6 py-5 flex items-center justify-between sticky top-0 z-10"
               style="background: linear-gradient(135deg, rgba(99,102,241,0.22) 0%, rgba(124,58,237,0.16) 100%); border-bottom: 1px solid rgba(129,140,248,0.25)">
            <div class="flex items-center gap-4 min-w-0">
              <div class="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                   style="background: rgba(99,102,241,0.25); border: 1px solid rgba(129,140,248,0.4)">
                <component :is="getIconComponent(selectedCategory.icon)" class="w-5 h-5 text-indigo-200" />
              </div>
              <div class="min-w-0">
                <h2 class="text-lg font-bold text-white truncate">{{ selectedCategory.title }}</h2>
                <p class="text-xs text-indigo-200/70 truncate">{{ selectedCategory.description }}</p>
              </div>
            </div>
            <button @click="closeModal"
                    class="p-2 rounded-lg text-indigo-200/70 hover:text-white hover:bg-white/10 transition-colors shrink-0">
              <X class="w-5 h-5" />
            </button>
          </div>

          <!-- Modal body -->
          <div class="p-6">
            <!-- Article view -->
            <div v-if="selectedArticle">
              <button @click="selectedArticle = null"
                      class="flex items-center gap-2 text-sm font-medium text-indigo-400 hover:text-indigo-300 transition-colors mb-6">
                <ArrowLeft class="w-4 h-4" />
                Back to articles
              </button>

              <article>
                <h1 class="text-2xl font-bold text-slate-100 mb-5">{{ selectedArticle.title }}</h1>
                <div class="article-content leading-relaxed"
                     v-html="renderMarkdown(selectedArticle.content)"></div>
              </article>
            </div>

            <!-- Article list -->
            <div v-else class="space-y-3">
              <button v-for="article in selectedCategory.articles" :key="article.id"
                      @click="selectArticle(article)"
                      class="w-full rounded-xl p-4 text-left transition-all duration-200 hover:bg-indigo-500/[0.07] group"
                      style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06)">
                <div class="flex items-start gap-4">
                  <div class="ep-stat-icon ep-stat-indigo !w-10 !h-10 !rounded-xl shrink-0">
                    <FileText class="w-[1.125rem] h-[1.125rem]" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="text-sm font-semibold text-slate-200 group-hover:text-indigo-300 transition-colors">
                      {{ article.title }}
                    </h3>
                    <div class="flex flex-wrap gap-1.5 mt-2">
                      <span v-for="tag in article.tags" :key="tag"
                            class="px-2 py-0.5 text-[10px] font-medium rounded-full text-indigo-300"
                            style="background: rgba(99,102,241,0.12); border: 1px solid rgba(99,102,241,0.25)">
                        {{ tag }}
                      </span>
                    </div>
                  </div>
                  <ChevronRight class="w-[1.125rem] h-[1.125rem] text-slate-600 shrink-0 group-hover:text-indigo-400 transition-colors mt-1" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  BookOpen,
  Search,
  X,
  FileText,
  HelpCircle,
  AlertCircle,
  Lightbulb,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  AlertTriangle,
  Zap,
  Bell,
  Clock,
  Calendar,
  Download,
  User,
} from 'lucide-vue-next';
import userManualService from '@/services/userManualService';
import { marked } from 'marked';

const router = useRouter();

// State
const loading = ref(true);
const searchQuery = ref('');
const searchResults = ref(null);
const categories = ref([]);
const faqs = ref([]);
const quickTips = ref([]);
const troubleshooting = ref([]);
const selectedCategory = ref(null);
const selectedArticle = ref(null);
const expandedFAQs = ref([]);
const selectedFAQCategory = ref('all');

// FAQ Categories for filtering
const faqCategories = [
  { label: 'All', value: 'all' },
  { label: 'Attendance', value: 'attendance' },
  { label: 'Leave', value: 'leave' },
  { label: 'Overtime', value: 'overtime' },
  { label: 'Profile', value: 'profile' },
  { label: 'General', value: 'general' },
];

// Computed
const filteredFAQs = computed(() => {
  if (selectedFAQCategory.value === 'all') {
    return faqs.value;
  }
  return faqs.value.filter(faq => faq.category === selectedFAQCategory.value);
});

// Methods
const loadUserManual = async () => {
  try {
    loading.value = true;
    const data = await userManualService.getAllData();

    categories.value = data.categories || [];
    faqs.value = data.faqs || [];
    quickTips.value = data.quickTips || [];
    troubleshooting.value = data.troubleshooting || [];
  } catch (error) {
    console.error('Error loading user manual:', error);
  } finally {
    loading.value = false;
  }
};

const handleSearch = async () => {
  if (!searchQuery.value || searchQuery.value.trim().length === 0) {
    searchResults.value = null;
    return;
  }

  try {
    const results = await userManualService.search(searchQuery.value);
    searchResults.value = results;
  } catch (error) {
    console.error('Error searching:', error);
  }
};

const clearSearch = () => {
  searchQuery.value = '';
  searchResults.value = null;
};

const selectCategory = category => {
  selectedCategory.value = category;
  selectedArticle.value = null;
};

const selectArticle = article => {
  selectedArticle.value = article;
};

const closeModal = () => {
  selectedCategory.value = null;
  selectedArticle.value = null;
};

const openArticle = (categoryId, articleId) => {
  const category = categories.value.find(cat => cat.id === categoryId);
  if (category) {
    const article = category.articles.find(art => art.id === articleId);
    selectCategory(category);
    if (article) {
      selectArticle(article);
    }
  }
  clearSearch();
};

const openFAQ = faqId => {
  if (!expandedFAQs.value.includes(faqId)) {
    expandedFAQs.value.push(faqId);
  }
  clearSearch();

  // Scroll to FAQ
  setTimeout(() => {
    const element = document.getElementById(`faq-${faqId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 100);
};

const toggleFAQ = faqId => {
  const index = expandedFAQs.value.indexOf(faqId);
  if (index > -1) {
    expandedFAQs.value.splice(index, 1);
  } else {
    expandedFAQs.value.push(faqId);
  }
};

const getIconComponent = iconName => {
  const icons = {
    Rocket: BookOpen,
    Clock: Clock,
    Calendar: Calendar,
    FileText: FileText,
    User: User,
    Zap: Zap,
    Bell: Bell,
    Download: Download,
    Lightbulb: Lightbulb,
  };
  return icons[iconName] || FileText;
};

const renderMarkdown = content => {
  return marked(content);
};

// Lifecycle
onMounted(() => {
  loadUserManual();
});
</script>

<style scoped>
/* Article content (rendered markdown) — dark theme */
.article-content :deep(h1),
.article-content :deep(h2) {
  font-size: 1.375rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 1.5rem 0 1rem;
}

.article-content :deep(h3) {
  font-size: 1.125rem;
  font-weight: 600;
  color: #f1f5f9;
  margin: 1rem 0 0.75rem;
}

.article-content :deep(h4) {
  font-size: 1rem;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0.75rem 0 0.5rem;
}

.article-content :deep(p) {
  color: #94a3b8;
  margin-bottom: 1rem;
  line-height: 1.7;
}

.article-content :deep(ul),
.article-content :deep(ol) {
  color: #94a3b8;
  margin: 0 0 1rem 1.5rem;
}

.article-content :deep(li) {
  line-height: 1.7;
  margin-bottom: 0.4rem;
}

.article-content :deep(strong) {
  font-weight: 600;
  color: #e2e8f0;
}

.article-content :deep(code) {
  background: rgba(99, 102, 241, 0.12);
  border: 1px solid rgba(99, 102, 241, 0.2);
  padding: 0.15rem 0.45rem;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  font-family: ui-monospace, monospace;
  color: #a5b4fc;
}

.article-content :deep(pre) {
  background: rgba(2, 4, 12, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.07);
  color: #e2e8f0;
  padding: 1rem;
  border-radius: 0.75rem;
  overflow-x: auto;
  margin-bottom: 1rem;
}

.article-content :deep(pre code) {
  background: transparent;
  border: none;
  padding: 0;
  color: inherit;
}

.article-content :deep(blockquote) {
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding-left: 1rem;
  font-style: italic;
  color: #94a3b8;
  margin: 1rem 0;
}

.article-content :deep(a) {
  color: #818cf8;
  text-decoration: underline;
}

.article-content :deep(a:hover) {
  color: #a5b4fc;
}
</style>
